<script setup lang="ts">
import { useWheelPicker } from '@ui/utils/useWheelPicker'
import { computed, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    label?: string
    step?: number
    options?: number[]
    loop?: boolean
  }>(),
  {
    label: 'general.time',
    step: 30,
    loop: true,
  },
)

const model = defineModel<number>({ required: true })
const minutesInDay = 24 * 60
const rowHeight = 58
const step = computed(() => (Number.isFinite(props.step) && props.step > 0 ? Math.max(1, Math.round(props.step)) : 30))
const origin = ref(model.value)
let expectedTime = model.value

const bounds = computed(() => {
  if (props.loop) {
    return undefined
  }
  if (props.options) {
    const originIndex = Math.max(0, props.options.indexOf(origin.value))
    return { min: -originIndex, max: Math.max(0, props.options.length - 1) - originIndex }
  }
  return { min: Math.ceil(-origin.value / step.value), max: Math.floor((minutesInDay - 1 - origin.value) / step.value) }
})

const { position, dragging, shift, reset, onPointerDown, onPointerMove, onPointerEnd, onWheel } = useWheelPicker({
  rowHeight,
  getBounds: () => bounds.value,
  onSelect(index) {
    if (props.options && !props.options.length) {
      return
    }
    expectedTime = timeAt(index)
    model.value = expectedTime
  },
})

const rows = computed(() => {
  const center = Math.round(position.value)
  return Array.from({ length: 7 }, (_, i) => center + i - 3)
    .filter(index => !bounds.value || (index >= bounds.value.min && index <= bounds.value.max))
    .map(index => {
      const offset = index - position.value
      return {
        index,
        time: props.options && !props.options.length ? '—:—' : formatTime(timeAt(index)),
        selected: index === center,
        style: {
          transform: `translateY(${offset * rowHeight}px) perspective(300px) rotateX(${-offset * 24}deg) scale(${Math.max(0.8, 1 - Math.abs(offset) * 0.08)})`,
          opacity: Math.max(0, 1 - Math.abs(offset) * 0.48),
        },
      }
    })
})

function timeAt(index: number) {
  if (props.options?.length) {
    const originIndex = Math.max(0, props.options.indexOf(origin.value))
    const count = props.options.length
    if (!props.loop) {
      return props.options[Math.max(0, Math.min(count - 1, originIndex + index))]!
    }
    return props.options[(((originIndex + index) % count) + count) % count]!
  }
  return normalizeTime(origin.value + index * step.value)
}

function normalizeTime(minutes: number) {
  return ((minutes % minutesInDay) + minutesInDay) % minutesInDay
}

function formatTime(minutes: number) {
  const normalized = normalizeTime(minutes)
  return `${String(Math.floor(normalized / 60)).padStart(2, '0')}:${String(normalized % 60).padStart(2, '0')}`
}

watch(
  [model, step, () => props.options, () => props.loop],
  ([value, currentStep, options, loop], [, previousStep, previousOptions, previousLoop]) => {
    if (
      value === expectedTime
      && currentStep === previousStep
      && options?.join() === previousOptions?.join()
      && loop === previousLoop
    ) {
      return
    }
    reset()
    origin.value = value
    expectedTime = value
  },
)
</script>

<template lang="pug">
.time-sheet-content.flex.flex-1.p-0-20-16.min-h-0
  .time-wheel.w-100p.h-174.position-relative.overflow-hidden.cursor-grab(
    :class="{ 'is-dragging': dragging }"
    role="spinbutton"
    :aria-label="$t(label)"
    :aria-valuemin="options?.[0] ?? 0"
    :aria-valuemax="options?.at(-1) ?? 1439"
    :aria-valuenow="normalizeTime(model)"
    :aria-valuetext="formatTime(model)"
    tabindex="0"
    @wheel.prevent="onWheel"
    @keydown.up.prevent="shift(-1)"
    @keydown.down.prevent="shift(1)"
    @pointerdown.stop="onPointerDown"
    @pointermove.stop="onPointerMove"
    @pointerup.stop="onPointerEnd"
    @pointercancel.stop="onPointerEnd"
    @lostpointercapture="onPointerEnd"
  )
    .time-selection.position-absolute.t-50p.l-50p.w-132.h-44.br-24
    .time-option.flex.row-16.typ-18-26-600.position-absolute.t-58.l-0.w-100p.h-58.align-items-center.justify-content-center(
      v-for="row in rows"
      :key="row.index"
      :class="{ selected: row.selected }"
      :style="row.style"
      aria-hidden="true"
    )
      span.flex-1.text-right {{ row.time.slice(0, 2) }}
      span.text-center :
      span.flex-1.text-left {{ row.time.slice(3) }}
</template>

<style scoped>
.time-wheel {
  touch-action: none;
  user-select: none;

  &.is-dragging {
    --uno: cursor-grabbing;
  }

  &:focus-visible {
    outline: 2px solid var(--cue-border-primary-soft);
    outline-offset: 2px;
  }

  &::before,
  &::after {
    content: '';
    pointer-events: none;
    --uno: position-absolute z-index-1 l-0 r-0 h-29;
  }

  &::before {
    background: linear-gradient(to bottom, var(--cue-bg-surface-primary), transparent);
    --uno: t-0;
  }

  &::after {
    background: linear-gradient(to top, var(--cue-bg-surface-primary), transparent);
    --uno: b-0;
  }
}

.time-selection {
  transform: translate(-50%, -50%);
  border: 1px solid var(--cue-border-primary-soft);
  background: var(--cue-bg-primary-soft);
  pointer-events: none;
}

.time-option {
  color: var(--cue-content-neutral-secondary);
  pointer-events: none;
  font-variant-numeric: tabular-nums;

  &.selected {
    color: var(--cue-content-primary-default);
  }
}
</style>
